import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  pcmpLookupDepartmentSchema,
  pcmpLookupDepartmentsSchema,
  pcmpLookupUsersSchema,
  type PcmpLookupDepartment,
  type PcmpLookupUser,
  type PcmpLookupUserKind,
} from '../types/pcmpLookups.types';
import type { PcmpDivision } from '../types/pcmpQueue.types';

/** The API lists nothing for a shorter term, so the pickers do not ask. */
export const PCMP_LOOKUP_MIN_CHARS = 2;

/** The API refuses a longer term: the search input caps it. */
export const PCMP_LOOKUP_MAX_CHARS = 100;

export const pcmpLookupQueryKeys = {
  all: () => ['pcmp-lookups'] as const,
  users: (kind: PcmpLookupUserKind, term: string) =>
    [...pcmpLookupQueryKeys.all(), kind, term] as const,
  departments: () => [...pcmpLookupQueryKeys.all(), 'departments'] as const,
};

/** At most 20 users by name, HR id or username; admins find anyone, others their downline. */
export const pcmpLookupUsersQueryOptions = (kind: PcmpLookupUserKind, term: string) => {
  const q = term.trim();
  return queryOptions({
    queryKey: pcmpLookupQueryKeys.users(kind, q),
    queryFn: async (): Promise<PcmpLookupUser[]> => {
      const res = await apiClient.get(API.pcmpLookup[kind], { params: { q } });
      return pcmpLookupUsersSchema.parse(res.data);
    },
    enabled: q.length >= PCMP_LOOKUP_MIN_CHARS,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });
};

/** Every region department with its PCMP division; narrow by division on the client. */
export const pcmpLookupDepartmentsQueryOptions = () =>
  queryOptions({
    queryKey: pcmpLookupQueryKeys.departments(),
    queryFn: async (): Promise<PcmpLookupDepartment[]> => {
      const res = await apiClient.get(API.pcmpLookup.departments);
      return pcmpLookupDepartmentsSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

/** Sets a department's PCMP division; null makes it "Not Mapped" (PCMP admins only). */
export async function updatePcmpDepartmentDivision(
  id: number,
  pcmpDivision: PcmpDivision | null,
): Promise<PcmpLookupDepartment> {
  const res = await apiClient.patch(API.pcmpDepartment.update(id), { pcmpDivision });
  return pcmpLookupDepartmentSchema.parse(res.data);
}
